// THE ORDER STAMP (« #1242 1/1 ») — readable after printing, whatever is under it.
//
// Owner's rule (2026-10-21):
//   · look at the REAL pixels under the stamp's exact box (plate colour + any
//     photo / sticker / text there), not the cover's base colour;
//   · average relative luminance of that box: light → pure black text (#000),
//     dark → pure white text (#FFF). Never grey, never "nearly opposite";
//   · busy / mixed box (big brightness spread, e.g. a photo edge) → a small
//     rounded box in the OPPOSITE colour behind the text (white box + black
//     text, or black box + white text);
//   · ≥ 7 pt, bold;
//   · on the cover: inside the board's safe zone — not in the bleed, not in
//     the turn-in that folds round the board, not on the spine. Visible on the
//     finished album.
//
// How the pixels are read: the page is drawn COMPLETELY first, then that one
// page is copied into a throw-away PDF and pdf.js (the engine the 3D preview
// and the render gate already use) draws ONLY the stamp's box. So whatever
// ends up there — plate, full-page back photo, a client's photo element, a
// sticker, an imported wrap — is what gets measured.
//
// The pure half of this file (luminance, decision, placement) is unit-tested in
// orderStamp.test.ts; the drawing half needs a browser (pdf.js + canvas) and
// falls back safely when it has none (see stampStyleWithoutPixels).
import { PDFDocument, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { TemplateLayout } from './assemblyTemplate';

const MM = 2.8346; // mm → pt, same constant as pdfGenerator / assemblyTemplate

/** Text size (pt). Owner's floor is 7 pt; 8 pt keeps a margin for the press. */
export const STAMP_FONT_PT = 8;
/** Distance from the board's bottom edge AND from its spine-side edge (mm).
 *  Wider than the editor's cover safe zone (CoverThumbnail SAFE_INSET ≈ 9–9.6
 *  mm from the 200 × 300 frame, i.e. ≤ 11.1 mm from the board edge), so the
 *  stamp stays readable even if the board sits a few mm off on the vinyl. */
export const STAMP_INSET_MM = 12;
/** Padding of the rounded box around the text (mm), and its corner radius. */
export const STAMP_PAD_X_MM = 1.2;
export const STAMP_PAD_Y_MM = 0.9;
export const STAMP_RADIUS_MM = 1;

/** Mean luminance above which the box counts as LIGHT (→ black text).
 *  0.179 is where black and white text have the SAME contrast ratio
 *  (√(1.05 × 0.05) − 0.05), so either side picks the better of the two. */
export const LIGHT_THRESHOLD = 0.179;
/** Busy when the luminance spread inside the box (p95 − p5) exceeds this. */
export const BUSY_SPREAD = 0.3;
/** Busy too when the chosen text colour falls under this contrast ratio
 *  against the box's worst 5 % (a dim textured photo with no hard edge). */
export const MIN_CONTRAST = 4.5;

export type Ink = 'black' | 'white';
export interface StampStyle {
  text: Ink;
  /** null = text alone; otherwise the rounded box behind it (always the opposite ink). */
  box: Ink | null;
  mean: number;
  p5: number;
  p95: number;
  /** Why this style — for the console line and the tests. */
  reason: string;
}

/** sRGB 0..255 channel → linear light 0..1. */
export function srgbToLinear(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of an 8-bit sRGB colour. */
export function relLuminance(r: number, g: number, b: number): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

export function contrast(l1: number, l2: number): number {
  const hi = Math.max(l1, l2), lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

/** Per-pixel luminance of an RGBA buffer (alpha composited over white paper). */
export function luminancesFromRgba(data: ArrayLike<number>): Float32Array {
  const n = Math.floor(data.length / 4);
  const out = new Float32Array(n);
  // 256-entry table — a 300-dpi box is tens of thousands of pixels.
  const lin = new Float32Array(256);
  for (let i = 0; i < 256; i++) lin[i] = srgbToLinear(i);
  for (let i = 0; i < n; i++) {
    const a = data[i * 4 + 3] / 255;
    const ch = (c: number) => Math.round(c * a + 255 * (1 - a));
    out[i] = 0.2126 * lin[ch(data[i * 4])] + 0.7152 * lin[ch(data[i * 4 + 1])] + 0.0722 * lin[ch(data[i * 4 + 2])];
  }
  return out;
}

function percentile(sorted: Float32Array | number[], p: number): number {
  if (!sorted.length) return 0;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))));
  return sorted[i];
}

/** THE DECISION. Pure: luminances in, ink + box out. */
export function decideStampStyle(lums: ArrayLike<number>): StampStyle {
  const arr = Float32Array.from(lums as ArrayLike<number>);
  if (!arr.length) return { text: 'black', box: 'white', mean: 1, p5: 1, p95: 1, reason: 'no pixels → boxed' };
  let sum = 0;
  for (let i = 0; i < arr.length; i++) sum += arr[i];
  const mean = sum / arr.length;
  arr.sort();
  const p5 = percentile(arr, 0.05), p95 = percentile(arr, 0.95);
  const text: Ink = mean > LIGHT_THRESHOLD ? 'black' : 'white';
  const opposite: Ink = text === 'black' ? 'white' : 'black';
  const spread = p95 - p5;
  // Worst case for the chosen ink: black text fears the darkest pixels, white the lightest.
  const worst = text === 'black' ? contrast(0, p5) : contrast(1, p95);
  if (spread > BUSY_SPREAD) return { text, box: opposite, mean, p5, p95, reason: `busy (spread ${spread.toFixed(2)} > ${BUSY_SPREAD})` };
  if (worst < MIN_CONTRAST) return { text, box: opposite, mean, p5, p95, reason: `low contrast (${worst.toFixed(1)}:1 < ${MIN_CONTRAST}:1)` };
  return { text, box: null, mean, p5, p95, reason: `${text === 'black' ? 'light' : 'dark'} (mean ${mean.toFixed(3)})` };
}

/** When the pixels can't be read (no browser, pdf.js failed): decide from the
 *  plate colour, and ALWAYS box — we did not see what is really there, and a
 *  boxed stamp is readable over anything. */
export function stampStyleWithoutPixels(plateHex: string | null | undefined): StampStyle {
  const h = String(plateHex ?? '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const ok = /^[0-9a-fA-F]{6}$/.test(full);
  const l = ok ? relLuminance(parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)) : 1;
  const text: Ink = l > LIGHT_THRESHOLD ? 'black' : 'white';
  return { text, box: text === 'black' ? 'white' : 'black', mean: l, p5: l, p95: l, reason: 'pixels unavailable → plate colour, boxed' };
}

export interface RectPt { x: number; y: number; w: number; h: number }
export interface StampPlacement {
  /** The rounded box (and the sampled area), PDF pt, origin bottom-left. */
  box: RectPt;
  /** Baseline origin for drawText. */
  textX: number;
  textY: number;
}

/** Box + baseline for a text of `textW` × (`ascent` + `descent`) pt whose box's
 *  bottom-left (or bottom-right, `anchor: 'right'`) corner sits at (ax, ay). */
export function placeStamp(ax: number, ay: number, textW: number, ascent: number, descent: number, anchor: 'left' | 'right'): StampPlacement {
  const padX = STAMP_PAD_X_MM * MM, padY = STAMP_PAD_Y_MM * MM;
  const w = textW + 2 * padX;
  const h = ascent + descent + 2 * padY;
  const x = anchor === 'right' ? ax - w : ax;
  return { box: { x, y: ay, w, h }, textX: x + padX, textY: ay + padY + descent };
}

/** COVER placement — back in its old spot (owner, 2026-10-23, after printing
 *  #WA-41692: « it's not in the middle centered, like it was before »): dead
 *  centre of the sheet, under the spine, in the middle of the bottom TURN-IN
 *  (between the sheet's bottom edge and the board's bottom edge). It folds
 *  under the board, as it did until 10-21; only the colour rule of 10-21 is
 *  kept (pure black / white from the real pixels, boxed when busy). From
 *  10-21 to 10-23 it sat on the back board, 12 mm inside, spine side. */
export function coverStampPlacement(L: TemplateLayout, textW: number, ascent: number, descent: number): StampPlacement {
  const w = textW + 2 * STAMP_PAD_X_MM * MM;
  const h = ascent + descent + 2 * STAMP_PAD_Y_MM * MM;
  return placeStamp(L.sheetCx - w / 2, L.boardBottom / 2 - h / 2, textW, ascent, descent, 'left');
}

/** Clearances of a box against the board it sits on (mm). All must be ≥ the
 *  inset for the stamp to be "in the safe zone"; used by the tests. */
export function boardClearancesMm(box: RectPt, L: TemplateLayout): { bottom: number; top: number; left: number; spineSide: number; offSpine: boolean } {
  return {
    bottom: (box.y - L.boardBottom) / MM,
    top: (L.boardTop - (box.y + box.h)) / MM,
    left: (box.x - L.backBoardL) / MM,
    spineSide: (L.backBoardR - (box.x + box.w)) / MM,
    offSpine: box.x + box.w <= L.spineColL,
  };
}

/** SVG path of a rounded rectangle, for pdf-lib's drawSvgPath (y down from the
 *  given origin, which is the rectangle's TOP-left corner). */
export function roundedRectPath(w: number, h: number, r: number): string {
  const k = Math.max(0, Math.min(r, w / 2, h / 2));
  return `M ${k} 0 H ${w - k} Q ${w} 0 ${w} ${k} V ${h - k} Q ${w} ${h} ${w - k} ${h} H ${k} Q 0 ${h} 0 ${h - k} V ${k} Q 0 0 ${k} 0 Z`;
}

// ─── Browser half ─────────────────────────────────────────────────────────────

const SAMPLE_PX_PER_PT = 300 / 72; // read the box at 300 dpi — the print resolution

/** Draw page `pageIndex` of `doc` — exactly as it stands now — and return the
 *  luminance of every pixel inside `rect`. Null when no browser / pdf.js. */
export async function sampleRegionLuminance(doc: PDFDocument, pageIndex: number, rect: RectPt): Promise<Float32Array | null> {
  if (typeof document === 'undefined') return null;
  // pdf-lib writes images and fonts into the file only on flush; a copy made
  // before it would point at nothing. flush() is idempotent and the print gate
  // calls it anyway right after.
  await doc.flush();
  const probe = await PDFDocument.create();
  const [copy] = await probe.copyPages(doc, [pageIndex]);
  probe.addPage(copy);
  const bytes = await probe.save();
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default;
  }
  const pdf = await pdfjs.getDocument({ data: bytes, isEvalSupported: false }).promise;
  try {
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: SAMPLE_PX_PER_PT });
    const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([rect.x, rect.y, rect.x + rect.w, rect.y + rect.h]);
    const left = Math.floor(Math.min(x1, x2)), top = Math.floor(Math.min(y1, y2));
    const w = Math.max(1, Math.ceil(Math.max(x1, x2)) - left);
    const h = Math.max(1, Math.ceil(Math.max(y1, y2)) - top);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff'; // paper
    ctx.fillRect(0, 0, w, h);
    // Only the box: shift the page so the box's top-left lands on (0, 0).
    await page.render({ canvasContext: ctx, viewport, transform: [1, 0, 0, 1, -left, -top] }).promise;
    page.cleanup();
    return luminancesFromRgba(ctx.getImageData(0, 0, w, h).data);
  } finally {
    await pdf.destroy();
  }
}

/**
 * Measure, decide, draw. `place` gets the text metrics and returns where the
 * box goes. Call it LAST on the page (after every photo, sticker and text),
 * before the print gate. Returns the style it used (logged by the caller).
 */
export async function drawOrderStamp(opts: {
  doc: PDFDocument;
  page: PDFPage;
  pageIndex: number;
  text: string;
  font: PDFFont;
  place: (textW: number, ascent: number, descent: number) => StampPlacement;
  /** The page's plate colour — used only if the pixels can't be read. */
  plateHex?: string | null;
  size?: number;
}): Promise<{ style: StampStyle; placement: StampPlacement; size: number }> {
  const size = Math.max(7, opts.size ?? STAMP_FONT_PT);
  const textW = opts.font.widthOfTextAtSize(opts.text, size);
  const ascent = opts.font.heightAtSize(size, { descender: false });
  const descent = Math.max(0, opts.font.heightAtSize(size) - ascent);
  const placement = opts.place(textW, ascent, descent);
  let style: StampStyle;
  try {
    const lums = await sampleRegionLuminance(opts.doc, opts.pageIndex, placement.box);
    style = lums && lums.length ? decideStampStyle(lums) : stampStyleWithoutPixels(opts.plateHex);
  } catch (e) {
    console.warn('[stamp] could not read the pixels under the order id:', (e as Error)?.message);
    style = stampStyleWithoutPixels(opts.plateHex);
  }
  const ink = (c: Ink) => (c === 'black' ? rgb(0, 0, 0) : rgb(1, 1, 1));
  if (style.box) {
    const { x, y, w, h } = placement.box;
    opts.page.drawSvgPath(roundedRectPath(w, h, STAMP_RADIUS_MM * MM), {
      x, y: y + h, color: ink(style.box), borderWidth: 0,
    });
  }
  opts.page.drawText(opts.text, { x: placement.textX, y: placement.textY, size, font: opts.font, color: ink(style.text) });
  return { style, placement, size };
}
